import { useSyncExternalStore } from 'react';

/**
 * Install-prompt plumbing for the PWA (ADR-012 Phase 4).
 *
 * Chrome fires `beforeinstallprompt` once, often before React mounts, so the
 * event is captured at module scope and stashed; the UI subscribes through
 * `useInstallPrompt`. Calling `promptInstall()` replays the stashed event.
 *
 * States the UI can be in:
 *  - `installable`  — the browser offered an install prompt we can trigger.
 *  - `installed`    — running standalone (or `appinstalled` fired).
 *  - `ios`          — iOS Safari: no prompt API; show "Add to Home Screen" help.
 *  - `unsupported`  — no prompt (yet) and not iOS: render nothing.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallState = 'installable' | 'installed' | 'ios' | 'unsupported';

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach(fn => fn());
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's non-standard flag
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function initInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

function getInstallState(): InstallState {
  if (installed || isStandalone()) return 'installed';
  if (deferredPrompt) return 'installable';
  if (isIos()) return 'ios';
  return 'unsupported';
}

/** Trigger the browser's install dialog. Resolves true if the user accepted. */
export async function promptInstall(): Promise<boolean> {
  const prompt = deferredPrompt;
  if (!prompt) return false;
  await prompt.prompt();
  const choice = await prompt.userChoice;
  // The event is one-shot; Chrome fires a fresh beforeinstallprompt later if
  // the user dismissed and remains eligible.
  deferredPrompt = null;
  notify();
  return choice.outcome === 'accepted';
}

export function useInstallPrompt(): InstallState {
  return useSyncExternalStore(
    callback => {
      subscribers.add(callback);
      return () => subscribers.delete(callback);
    },
    getInstallState,
    () => 'unsupported' as const
  );
}
